import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Owner-scoped, logout-safe cache for user-added categories and per-attribute
 * values entered via the Add Product "+ Add" modals. Keys use the
 * `bp_custom_*` prefix which is intentionally NOT touched by any logout
 * / token-clear code path, so the catalog survives sign-out.
 */

const categoriesKey = (ownerId: string) => `bp_custom_categories:${ownerId}`;
const attributeValuesKey = (ownerId: string) =>
  `bp_custom_attribute_values:${ownerId}`;

const uniqCi = (items: readonly string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
};

const normalizeAttributeKey = (attributeName: string): string =>
  attributeName.trim().toLowerCase();

export async function loadCustomCategories(
  ownerId: string,
): Promise<string[]> {
  if (!ownerId) return [];
  try {
    const raw = await AsyncStorage.getItem(categoriesKey(ownerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? uniqCi(parsed.map(String)) : [];
  } catch {
    return [];
  }
}

export async function addCustomCategory(
  ownerId: string,
  name: string,
): Promise<string[]> {
  const trimmed = String(name || '').trim();
  if (!ownerId || !trimmed) return loadCustomCategories(ownerId);
  const existing = await loadCustomCategories(ownerId);
  const next = uniqCi([...existing, trimmed]);
  try {
    await AsyncStorage.setItem(categoriesKey(ownerId), JSON.stringify(next));
  } catch {
    // best-effort persistence; UI keeps working on failure
  }
  return next;
}

export async function loadCustomAttributeValues(
  ownerId: string,
): Promise<Record<string, string[]>> {
  if (!ownerId) return {};
  try {
    const raw = await AsyncStorage.getItem(attributeValuesKey(ownerId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, string[]> = {};
    for (const [attrName, values] of Object.entries(parsed)) {
      if (Array.isArray(values)) {
        out[normalizeAttributeKey(attrName)] = uniqCi(values.map(String));
      }
    }
    return out;
  } catch {
    return {};
  }
}

export async function addCustomAttributeValue(
  ownerId: string,
  attributeName: string,
  value: string,
): Promise<Record<string, string[]>> {
  const trimmedName = String(attributeName || '').trim();
  const trimmedValue = String(value || '').trim();
  if (!ownerId || !trimmedName || !trimmedValue) {
    return loadCustomAttributeValues(ownerId);
  }
  const all = await loadCustomAttributeValues(ownerId);
  const key = normalizeAttributeKey(trimmedName);
  all[key] = uniqCi([...(all[key] || []), trimmedValue]);
  try {
    await AsyncStorage.setItem(
      attributeValuesKey(ownerId),
      JSON.stringify(all),
    );
  } catch {
    // best-effort persistence; UI keeps working on failure
  }
  return all;
}

/** Merge preload + product-derived + custom categories, case-insensitive. */
export function mergeCategoryOptions(
  preload: readonly string[],
  fromProducts: readonly string[],
  custom: readonly string[],
): string[] {
  return uniqCi([...preload, ...fromProducts, ...custom]);
}

/** Merge attribute-doc values + custom stored values for that attribute. */
export function mergeAttributeValues(
  serverValues: readonly string[],
  customForAttr: readonly string[],
): string[] {
  return uniqCi([...serverValues, ...customForAttr]);
}

export const __internal = { uniqCi, normalizeAttributeKey };
